import { useMemo } from 'react'

import type { PredictionEvent } from '@/services/prediction/types'

import { getAbsoluteScheduleDateLabel, getScheduleDateLabel } from '../utils/getScheduleDateLabel'

interface ScheduleLabel {
  /** Human-readable label, e.g. `"Today, 6:00 pm"` / `"Jun 29, 10:00 pm"`. */
  label: string
  /** ISO datetime backing the label — feed to `<time dateTime={…}>`. */
  datetime: string
}

/**
 * Port of cdc-web `EventCard/hooks/useScheduleLabel.ts`。
 *
 * 日期源按 `event_kind_asset_type` 切换（cdc-web `resolveScheduleSource`）：
 *   - predicts → `close_date`（用户关心"还能买多久"，即投注截止）；
 *   - sports 等其他 → `event_date`（开赛时刻）。
 * 首选日期缺失时退另一个字段（BE 数据兜底），都没有则返回空标签。
 *
 * `variant`：relative（默认）→ "Today"/星期/日期；absolute → 永远日历日期
 * （league 卡不显示 "Today"，OG-8545）。
 */
export function useScheduleLabel(
  event: PredictionEvent,
  variant: 'relative' | 'absolute' = 'relative',
): ScheduleLabel {
  const isPredicts = event.event_kind_asset_type === 'predicts'
  const primaryDate = isPredicts ? event.close_date : event.event_date
  const primaryConfirmed = isPredicts
    ? (event.close_time_confirmed ?? false)
    : (event.event_time_confirmed ?? false)
  const date = primaryDate ?? event.close_date ?? event.event_date ?? ''
  const timeConfirmed = primaryDate
    ? primaryConfirmed
    : (event.close_time_confirmed ?? event.event_time_confirmed ?? false)

  const formatLabel = () => {
    if (!date) return ''
    return variant === 'absolute'
      ? getAbsoluteScheduleDateLabel(date, timeConfirmed)
      : getScheduleDateLabel(date, timeConfirmed)
  }

  return useMemo(() => ({ label: formatLabel(), datetime: date }), [date, timeConfirmed, variant])
}
